(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  const SECTION_SELECTOR = [
    'main .shopify-section > .section',
    '.shopify-section-group-footer-group.icons-with-text > .section',
    '.shopify-section-group-footer-group > .section.footer'
  ].join(', ');

  const MEDIA_SELECTOR = [
    'main .section img',
    'main .section video',
    '.shopify-section-group-footer-group.icons-with-text .section img'
  ].join(', ');

  const ICON_CHILD_SELECTOR = '.icons_with_text__item';
  const PRODUCT_CARD_SELECTOR = '.product-grid .card-product';
  const COLS_HINT = 4; // stagger delay within a row

  let productObserver = null;

  const getTargets = () =>
    [...document.querySelectorAll(SECTION_SELECTOR)].filter((el) => {
      const parent = el.parentElement;
      if (parent && parent.classList.contains('custom-liquid')) return false;
      return true;
    });

  const forcePaint = (el) => {
    void el.offsetHeight;
  };

  const reveal = (el) => {
    forcePaint(el);
    el.classList.add('is-visible');
  };

  const revealAfter = (el, delayMs) => {
    window.setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => reveal(el));
      });
    }, delayMs);
  };

  const prepareFadeOnly = (el, duration) => {
    if (el.dataset.scrollFadeReady === 'true') return;
    el.dataset.scrollFadeReady = 'true';
    el.classList.add('scroll-fade', 'scroll-fade--fade-only');
    el.style.setProperty('--scroll-fade-duration', duration);
  };

  const prepareRise = (el, duration) => {
    if (el.dataset.scrollFadeReady === 'true') return;
    el.dataset.scrollFadeReady = 'true';
    el.classList.add('scroll-fade');
    el.style.setProperty('--scroll-fade-duration', duration);
  };

  const prepareIconsSection = (el) => {
    if (el.dataset.scrollFadeReady === 'true') return;
    el.dataset.scrollFadeReady = 'true';
    el.classList.add('scroll-fade', 'scroll-fade--fade-only');
    el.style.setProperty('--scroll-fade-duration', '0.9s');

    el.querySelectorAll(ICON_CHILD_SELECTOR).forEach((child, childIndex) => {
      if (child.dataset.scrollFadeChild === 'true') return;
      child.dataset.scrollFadeChild = 'true';
      child.classList.add('scroll-fade-child');
      child.style.setProperty('--scroll-fade-delay', `${Math.min(childIndex * 160, 480)}ms`);
    });
  };

  const revealSplitBlocks = (sectionEl) => {
    const blocks = [...sectionEl.querySelectorAll('.split-banner__block')];
    if (!blocks.length) return false;

    sectionEl.dataset.scrollFadeReady = 'true';
    revealAfter(blocks[0], 480);
    if (blocks[1]) revealAfter(blocks[1], 480);
    return true;
  };

  /** Product cards: CSS pre-hides them; JS only adds is-visible */
  const observeProductCards = () => {
    const cards = [...document.querySelectorAll(PRODUCT_CARD_SELECTOR)];
    if (!cards.length) return;

    if (!productObserver) {
      productObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            obs.unobserve(entry.target);
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -6% 0px',
          threshold: 0.08
        }
      );
    }

    cards.forEach((card, index) => {
      if (card.dataset.scrollFadeReady === 'true') return;
      card.dataset.scrollFadeReady = 'true';
      card.style.setProperty('--scroll-fade-delay', `${(index % COLS_HINT) * 70}ms`);
      productObserver.observe(card);
    });
  };

  /** Product detail page: media then info (CSS pre-hides; JS only reveals) */
  const revealProductPage = () => {
    const media = document.querySelector('.main-product .product__media-wrapper');
    const info = document.querySelector('.main-product .product__info-wrapper');
    if (!media && !info) return false;

    const section = document.querySelector(
      '.shopify-section.main-product-section > .section, .main-product'
    );
    if (section) {
      const sectionRoot = section.classList.contains('main-product')
        ? section.closest('.section') || section
        : section;
      sectionRoot.dataset.scrollFadeReady = 'true';
      sectionRoot.classList.add('is-visible');
    }

    if (media && media.dataset.scrollFadeReady !== 'true') {
      media.dataset.scrollFadeReady = 'true';
      revealAfter(media, 140);
    }
    if (info && info.dataset.scrollFadeReady !== 'true') {
      info.dataset.scrollFadeReady = 'true';
      revealAfter(info, 320);
    }
    return true;
  };

  const observeSections = () => {
    document.querySelectorAll('.shopify-section-group-footer-group.custom-liquid > .section').forEach((el) => {
      el.classList.remove('scroll-fade', 'scroll-fade--fade-only', 'is-visible');
      el.style.removeProperty('--scroll-fade-duration');
      delete el.dataset.scrollFadeReady;
    });

    document.querySelectorAll('.shopify-section.scroll-fade').forEach((section) => {
      section.classList.remove('scroll-fade', 'is-visible');
      section.style.removeProperty('--scroll-fade-duration');
    });

    const targets = getTargets();
    if (!targets.length) {
      observeProductCards();
      return;
    }

    const mainTargets = targets.filter((el) => el.closest('main'));
    const footerTargets = targets.filter((el) => !el.closest('main'));

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el, i) => {
        prepareRise(el, '1.3s');
        revealAfter(el, 120 + i * 160);
      });
      document.querySelectorAll(PRODUCT_CARD_SELECTOR).forEach((card, i) => revealAfter(card, 100 + i * 40));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.01
      }
    );

    mainTargets.forEach((el, index) => {
      // Product grids: animate cards, not the whole section
      if (el.querySelector(PRODUCT_CARD_SELECTOR)) {
        el.dataset.scrollFadeReady = 'true';
        el.classList.add('is-visible');
        return;
      }

      // Product page: animate media/info columns, keep section visible
      if (el.querySelector('.main-product') || el.classList.contains('product-overview-section')) {
        el.dataset.scrollFadeReady = 'true';
        el.classList.add('is-visible');
        return;
      }

      // Hero fade only on real slideshow, not on every first section
      const isHeroSlideshow =
        index === 0 &&
        (el.classList.contains('slideshow') || el.closest('.shopify-section.slideshow'));

      if (isHeroSlideshow) {
        prepareFadeOnly(el, '1.85s');
        revealAfter(el, 280);
        return;
      }

      if (revealSplitBlocks(el)) {
        return;
      }

      prepareRise(el, '1.35s');
      observer.observe(el);
    });

    footerTargets.forEach((el) => {
      if (el.classList.contains('footer')) {
        prepareFadeOnly(el, '1.1s');
      } else if (el.querySelector(ICON_CHILD_SELECTOR)) {
        prepareIconsSection(el);
      } else {
        prepareRise(el, '1.35s');
      }
      if (!el.classList.contains('is-visible')) observer.observe(el);
    });

    observeProductCards();
    revealProductPage();
  };

  const softLoadMedia = () => {
    document.querySelectorAll(MEDIA_SELECTOR).forEach((media) => {
      if (media.closest('main .shopify-section:first-child')) return;
      if (media.closest('.section-split-banner')) return;
      if (media.closest('.product-grid .card-product')) return;
      if (media.closest('.main-product')) return;

      if (media.dataset.scrollFadeBound === 'true') return;
      media.dataset.scrollFadeBound = 'true';
      media.classList.add('scroll-fade-media');

      const markLoaded = () => media.classList.add('is-loaded');

      if (media.tagName === 'IMG') {
        if (media.complete && media.naturalWidth > 0) markLoaded();
        else {
          media.addEventListener('load', markLoaded, { once: true });
          media.addEventListener('error', markLoaded, { once: true });
        }
      } else if (media.tagName === 'VIDEO') {
        if (media.readyState >= 2) markLoaded();
        else {
          media.addEventListener('loadeddata', markLoaded, { once: true });
          media.addEventListener('error', markLoaded, { once: true });
        }
      } else {
        markLoaded();
      }
    });
  };

  const init = () => {
    observeSections();
    softLoadMedia();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Collection filters / pagination / infinite load often replace the grid
  const gridRoot = document.querySelector('main');
  if (gridRoot && 'MutationObserver' in window) {
    let scheduled = false;
    const mo = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        observeProductCards();
      });
    });
    mo.observe(gridRoot, { childList: true, subtree: true });
  }
})();
